// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/growth-score/growth-score-content.ts (ADAPT)
// ADAPTED (owner rule: TP's onboarding is a prototype, we copy its look, never its data): TP's per-check texts quoted its
// Shapermint sample (217K visits, "52% of new ads", "9 of 16 competitors", sample brands, vendors, commissions). Here every
// text keeps what the check MEANS and why it matters, and carries no number, brand, vendor or claim about the brand.
// The only figures are the BFF's own cohort strips (`CopyContext.strip`), quoted when the BFF sent one.
/**
 * The words of the Growth Score for a check the BFF sent no opportunity for: what we look at, the lever and the agent
 * action, keyed by stable check keys.
 */
import type { AdMetricKey, Opportunity } from './growth-score.types'

export interface CopyContext {
  /** The brand's name, from our own data. */
  brand: string
  /** The brand's own value and the cohort median of an ad metric, from the BFF's strips; undefined when it sent none. */
  strip: (key: AdMetricKey) => { you: number; median: number } | undefined
  upsell: 'yes' | 'no' | 'unsure'
  subscribes: boolean
}

type Lever = NonNullable<Opportunity['lever']>
/** No brands "already doing it" (we have none of ours to name here) and no link (it was never wired). */
const lever = (title: string, body: string): Lever => ({ title, body, alreadyDoing: [], link: '' })
const agent = (label: string, description: string) => ({ label, description })

/** Shared levers and actions: several checks open the same move. */
const L = {
  adVolume: lever('Spend the volume on new angles, not repeats', 'Turn each of your best ads into new hooks, instead of starting new concepts from zero.'),
  tiktokShop: lever(
    'Open TikTok Shop and pay creators to sell',
    "Open it with your top products and send free samples to creators for a commission on what they sell. Their videos reach buyers your account doesn't.",
  ),
  broadcasting: lever('Swap some discount emails for content', 'Fit guides, customer stories and new colors, in place of part of the discounts.'),
  omnichannel: lever('Test a second paid channel with your best Meta videos', 'Start with the videos that already work on Meta: no new creative needed.'),
}
const A = {
  tiktokOrganic: agent('Build an organic TikTok strategy', 'A content plan built on what the cohort and its creators post on TikTok.'),
  broadcasting: agent('Plan a month of content emails', 'Weeks of content emails drawn from your best organic posts and reviews.'),
  omnichannel: agent('Plan a channel test', 'A short test of your best Meta videos on another channel, with a budget.'),
}

/** "You X, cohort median Y" for an ad metric, from the BFF's strip; undefined when it sent none. */
const versus = (c: CopyContext, key: AdMetricKey, line: (you: number, median: number) => string): string | undefined => {
  const s = c.strip(key)
  return s ? line(s.you, Math.round(s.median)) : undefined
}

/** Every check's opportunity, by check key. */
export function opportunityFor(key: string, c: CopyContext): Opportunity | undefined {
  switch (key) {
    /* Ads */
    case 'creative_diversity':
      return {
        summary: 'How varied your top ads are.',
        whatWeSee: "Varied angles and formats in your top ads reach different buyers. More of the same angle competes for the same buyers.",
        lever: lever("Add the angles you don't use", "Before launching more, test the angles your cohort wins with and you don't run yet."),
        agent: agent("Research the angles you're missing", "New angles to test, drawn from the ones your cohort uses and your ads don't."),
      }
    case 'winners_that_last': {
      const kept = versus(c, 'kept7', (you, md) => `${you}% of your new ads are still live after 7 days; the cohort median is ${md}%.`)
      const age = versus(c, 'topdl', (you, md) => `Your top ads are ${you} days old; the cohort median is ${md}.`)
      return {
        summary: 'How long your new ads stay live.',
        whatWeSee:
          [kept, age].filter(Boolean).join(' ') ||
          'Whether your new ads are still live after 7 days, and how old your top ads are. An ad paused early never gets the time to prove itself.',
        lever: lever('Give new ads time to prove themselves', 'Set one rule for when an ad can be paused, such as a minimum run time or a cost per purchase limit.'),
        agent: agent('Flag ads paused early', 'The ads paused early, with what each one did.'),
      }
    }
    case 'testing_velocity':
      return {
        summary: 'How many new ads you launch.',
        whatWeSee:
          versus(c, 'lpw', (you, md) => `You launch ${you} new ads a week; the cohort median is ${md}.`) ??
          'How many new ads you launch each week, set against your cohort. Volume only helps when what you launch is different.',
        lever: L.adVolume,
        agent: agent('Find ad ideas from your cohort', `Ideas for ${c.brand} drawn from the cohort's longest-running ads.`),
      }
    case 'audience_fit':
      return {
        summary: 'Whether your ads show the people who buy from you.',
        whatWeSee: 'Whether the people in your top ads are the people who buy from you.',
        lever: lever('Cast the customer who buys from you', 'Show them early in the ad, in their own words.'),
        agent: agent('Find creators who look like your buyer', 'Creators and ads to borrow from, ranked by fit with your customer profile.'),
      }
    case 'partnership_ads':
      return {
        summary: "How many ads run from a creator's handle.",
        whatWeSee:
          versus(c, 'psh', (you, md) => `${you}% of your top ads run as partnership ads; the cohort median is ${md}%.`) ??
          "How many of your top ads run as partnership ads, from a creator's handle.",
        lever: lever(
          "Run creator videos from the creator's handle",
          'Ask the creators already posting about you for partnership access and run their best posts as ads from their account.',
        ),
        agent: agent('Find creators for partnership ads', 'Creators who already post about your category, ranked by fit with your buyer.'),
      }

    /* Funnel */
    case 'aov_levers':
      return {
        summary: 'How you raise the value of an order.',
        whatWeSee: 'The levers that lift order value: a bundle, a multi-unit discount and a free-shipping bar.',
        lever: lever(
          "Add the order-value levers you're missing",
          'Add a multi-unit discount and a free-shipping bar set a little above your average order.',
        ),
        agent: agent('Draft your bundle and free-shipping offer', "A multi-unit offer and a free-shipping threshold, proposed from your prices and your cohort's offers."),
      }
    case 'landing_strategy':
      return {
        summary: 'Where your ads land.',
        whatWeSee: 'Paid traffic can land on a page built for the ad, with the offer in the first screen, or on a generic product page.',
        lever: lever('Send ads to a page built for the ad', 'One landing page per top angle, with the offer in the first screen next to the photo.'),
        agent: agent('Mock up a landing page for your top ad', 'A first screen built for your best ad, with the offer in view.'),
      }
    case 'checkout_friction':
      return {
        summary: 'How many steps stand between the ad and payment.',
        whatWeSee: 'The steps from ad to payment, and the ways to pay on the first screen. Each extra step costs buyers.',
        lever: lever('Cut a step and add express pay', 'Take out the steps that are not needed and offer express payment on the first screen.'),
        agent: agent('Map your checkout against your cohort', 'A list of the checkout steps you can remove, as a buyer sees them on a phone.'),
      }
    case 'post_purchase_upsell':
      return {
        summary: 'Whether you make an offer after payment.',
        whatWeSee: `A one-click offer after payment that does not ask for the card again.${
          c.upsell === 'yes' ? ' You told us you run one.' : c.upsell === 'no' ? " You told us you don't run one." : ''
        }`,
        lever:
          c.upsell === 'no'
            ? lever('Add a one-click offer after payment', 'Offer a second unit on the thank-you page, without asking for the card again.')
            : lever('Add a second unit before the cart', 'You already upsell after payment. Also offer "add a second color" on the product page.'),
        agent: agent('Draft a post-purchase offer', 'The product to offer, and the offer text for the thank-you page.'),
      }
    case 'subscription':
      return {
        summary: c.subscribes ? 'You offer subscribe & save.' : 'Whether you offer subscribe & save.',
        whatWeSee: c.subscribes
          ? 'You told us you offer it. Where it sits on the product page changes how many buyers see it.'
          : 'Subscribe & save on the product page brings buyers back without another ad.',
        lever: lever('Offer subscribe & save on basics', 'A discount for a regular refill of everyday pieces, shown under the price.'),
        agent: agent('Draft a subscribe & save offer', 'The products that repeat, with a proposed discount and cadence.'),
      }

    /* TikTok */
    case 'tiktok_shop':
      return {
        summary: 'Whether you sell on TikTok Shop.',
        whatWeSee: 'On TikTok the shop is where creators get paid to sell for you.',
        lever: L.tiktokShop,
        agent: A.tiktokOrganic,
      }
    case 'creators':
      return {
        summary: 'How many creators sell or talk about you.',
        whatWeSee: 'Creators who mention you, and creators who sell for you on commission, reach buyers your own account does not.',
        lever: lever('Pay creators to sell for you', 'Free samples plus a commission for creators in your niche.'),
        agent: agent('Find creators to sell for you', 'Creators in your category, ranked by fit with your buyer, with the outreach drafted.'),
      }
    case 'account_reach':
      return {
        summary: 'How many people see what you post.',
        whatWeSee: 'How often you post, and the views your videos get, set how many people see you.',
        lever: lever('Post in the formats that travel', "Copy the formats that get your cohort the most views instead of product clips."),
        agent: A.tiktokOrganic,
      }
    case 'tiktok_ads':
      return {
        summary: 'How many Spark Ads you run.',
        whatWeSee: 'Spark Ads boost creator posts that already work organically.',
        lever: lever('Boost the creator posts that already work', 'Run your best-performing organic creator posts as Spark Ads before making new TikTok creative.'),
        agent: agent('Pick posts to run as Spark Ads', 'Creator posts about you, ranked by organic performance and fit with your buyer.'),
      }
    case 'live_shopping':
      return {
        summary: 'No score yet.',
        whatWeSee: "LIVE shopping has no score yet, so it doesn't count toward your score.",
      }

    /* Search & AI */
    case 'seo':
      return {
        summary: 'How much of your search traffic comes without your name.',
        whatWeSee: "Organic traffic from searches that don't include your brand name is how shoppers who don't know you yet find you.",
        lever: lever('Build pages for the searches you miss', "Size guides and comparison pages are where shoppers who don't know you yet find a brand."),
        agent: agent('Find the pages you should write', "The searches where competitors rank and you don't, grouped into pages to write."),
      }
    case 'sem':
      return {
        summary: 'Whether you hold your own name in paid search.',
        whatWeSee: 'When others bid on searches for your brand name, their ads take clicks from people already looking for you.',
        lever: lever('Bid on your own name', 'A brand campaign keeps the visits from people who already know you.'),
        agent: agent('Plan a brand-name campaign', 'A brand campaign with a budget to win back searches for your name.'),
      }
    case 'ai':
      return {
        summary: 'How often AI search and assistants quote you.',
        whatWeSee: 'AI answers that cite you, and visits that come from AI assistants, are how shoppers find you there.',
        lever: lever(
          'Get onto the pages AI quotes',
          'Assistants quote comparison pages, forum threads and "best of" lists. Publish fit guides and get into the lists they cite.',
        ),
        agent: agent('Find the pages AI quotes', 'A list of the pages AI assistants cite, instead of you, when shoppers ask about your category.'),
      }
    case 'agent':
      return {
        summary: 'Whether an AI agent can find your products and pay.',
        whatWeSee: 'An AI agent that buys for a shopper has to read your product data and complete a checkout.',
        lever: lever('Make your products readable to agents', 'Keep your product feed complete, with price and stock, and keep checkout reachable by an agent.'),
        agent: agent('Watch your agent setup', 'A scheduled check of your agent readiness, with a note when something breaks.'),
      }

    /* Retention */
    case 'welcome_series':
      return {
        summary: 'How long your welcome series runs.',
        whatWeSee: 'A longer welcome series in the first days gives a new subscriber more reasons to buy.',
        lever: lever('Stretch the welcome series', 'Use the first days for the offer, a fit guide, customer reviews, best sellers and a last call.'),
        agent: agent('Write a longer welcome series', "A welcome series in your brand voice, modeled on the cohort's."),
      }
    case 'abandoned_cart':
      return {
        summary: 'No score yet.',
        whatWeSee: "Abandoned cart has no score yet, so it doesn't count toward your score.",
      }
    case 'discount_dependence':
      return {
        summary: 'How much of your email is a discount.',
        whatWeSee:
          'The share of your broadcasts that carry a discount, and how often you send. Content emails (fit guides, stories) are the alternative.',
        lever: L.broadcasting,
        agent: A.broadcasting,
      }
    case 'sms_capture':
      return {
        summary: 'Whether you ask for a phone number.',
        whatWeSee: 'Whether your signup pop-up asks for a phone number in a second step.',
        lever: lever('Ask for a phone number at signup', 'Add a second step to the pop-up: the phone number, for an extra offer. That is how an SMS list starts.'),
        agent: agent('Draft a 2-step signup pop-up', "A signup pop-up with the phone step, modeled on the cohort's."),
      }

    /* Omnichannel */
    case 'paid_channels':
      return {
        summary: 'Which paid channels you use beyond Meta.',
        whatWeSee: 'Which paid channels you use beyond Meta, set against the ones your cohort uses.',
        lever: L.omnichannel,
        agent: A.omnichannel,
      }
    case 'meta_concentration':
      return {
        summary: 'How much of your advertising is on Meta.',
        whatWeSee: 'The share of your ads that run on Meta, set against your cohort. With one platform, one change hits you harder.',
        lever: lever(
          'Move part of the Meta budget to a second channel',
          'Start with the channel your cohort uses most after Meta and Google, with your best Meta videos.',
        ),
        agent: A.omnichannel,
      }
    case 'amazon':
      return {
        summary: 'Whether you sell on Amazon.',
        whatWeSee: 'Amazon is another place shoppers can find and buy from you.',
      }
    default:
      return undefined
  }
}
